import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Menu from "../components/menu";
import ProfileInfo from "../components/profile-info";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import ContentLoader from "../../components/ContentLoader";
import { useAuth } from "../../auth/AuthContext";
import { useMenu } from "../../Common/MenuContext";
import { resolveMenuPathForUser } from "../../utils/menuRouteUtils";
import "../../assets/css/dashboard.css";

interface QuickLink {
  label: string;
  path: string;
  alertKey?: "capability" | "projectSetup";
}

function getAlertKey(path: string, label: string): QuickLink["alertKey"] {
  const pathKey = path.toLowerCase();
  const labelKey = label.toLowerCase();
  if (
    pathKey.includes("capability_approval") ||
    labelKey.includes("capability request")
  ) {
    return "capability";
  }
  if (
    pathKey.includes("project_setup_approval") ||
    labelKey.includes("project setup")
  ) {
    return "projectSetup";
  }
  return undefined;
}

function collectQuickLinks(
  menuItems: Array<Record<string, unknown>>,
  roleKey: string
): QuickLink[] {
  const links: QuickLink[] = [];

  const visit = (item: Record<string, unknown>) => {
    if (item.isCategory) {
      const subMenus = Array.isArray(item.subMenus) ? item.subMenus : [];
      for (const sub of subMenus) {
        visit(sub as Record<string, unknown>);
      }
      return;
    }

    const label = String(item.displayName || item.menuName || item.name || "").trim();
    const path = resolveMenuPathForUser(item, roleKey);

    if (path && path !== "/admin/dashboard" && label) {
      links.push({
        label,
        path,
        alertKey: getAlertKey(path, label),
      });
    }

    const subMenus = Array.isArray(item.subMenus) ? item.subMenus : [];
    for (const sub of subMenus) {
      visit(sub as Record<string, unknown>);
    }
  };

  for (const item of menuItems) {
    visit(item);
  }

  return links;
}

const AdminDashboard = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const { user } = useAuth();
  const {
    menuItems,
    loadingMenu,
    hasPendingCapabilityApprovals,
    hasPendingProjectSetup,
    pendingCapabilityCount,
    pendingProjectSetupCount,
  } = useMenu();
  const [selectedItem, setSelectedItem] = useState(null);
  const roleKey = "admin";

  const quickLinks = useMemo(
    () => collectQuickLinks(menuItems, roleKey),
    [menuItems]
  );

  const pendingTotal =
    (pendingCapabilityCount ?? 0) + (pendingProjectSetupCount ?? 0);
  const hasPendingApprovals =
    Boolean(hasPendingCapabilityApprovals) || Boolean(hasPendingProjectSetup);

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={(item) => {
            setSelectedItem(item);
            return item;
          }}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <div className="dash-frame-parent">
        <div className="dash-frame-group">
          <div className="dash-dashboard-wrapper">
            <div className="dash-dashboard app-heading-accent text-[var(--app-heading)]">Admin Dashboard</div>
          </div>
          <ProfileInfo />
        </div>

        <div className="dash-frame-wrapper">
          <div className="dash-frame-div">
            <div className="dash-admin-home">
              <p className="dash-admin-home__welcome">
                Welcome, {user?.userName || "Admin"}!
              </p>
              <p className="dash-admin-home__subtitle">
                Select a section from the sidebar or use the shortcuts below.
                Only areas assigned to your role are shown.
              </p>

              {hasPendingApprovals && (
                <div
                  className="dash-role-stats dash-admin-home__pending"
                  aria-live="polite"
                >
                  <div className="dash-role-stat dash-role-stat--accent dash-role-stat--alert">
                    <span className="dash-role-stat__label">
                      Pending approvals
                      <span
                        className="dash-menu-new-badge"
                        title="Requests waiting for review"
                      >
                        <span
                          className="dash-menu-new-badge__dot"
                          aria-hidden="true"
                        />
                        New
                      </span>
                    </span>
                    <span className="dash-role-stat__value">
                      {pendingTotal.toLocaleString()}
                    </span>
                    <span className="dash-role-stat__hint">
                      {pendingCapabilityCount.toLocaleString()} capability ·{" "}
                      {pendingProjectSetupCount.toLocaleString()} project setup
                    </span>
                  </div>
                </div>
              )}

              {hasPendingApprovals && (
                <div className="dash-role-actions">
                  {hasPendingCapabilityApprovals && (
                    <Link
                      to="/sa/capability_approval"
                      className="dash-role-action-link dash-role-action-link--alert"
                    >
                      Review capability requests →
                    </Link>
                  )}
                  {hasPendingProjectSetup && (
                    <Link
                      to="/sa/project_setup_approval"
                      className="dash-role-action-link dash-role-action-link--alert"
                    >
                      Review project setup requests →
                    </Link>
                  )}
                </div>
              )}

              {loadingMenu ? (
                <ContentLoader compact label="Loading your access…" />
              ) : quickLinks.length === 0 ? (
                <p className="dash-admin-home__loading">
                  No management sections are available for your account yet.
                  Contact a Super Admin to assign permissions.
                </p>
              ) : (
                <div className="dash-admin-home__grid">
                  {quickLinks.map((link) => {
                    const isAlert =
                      (link.alertKey === "capability" &&
                        hasPendingCapabilityApprovals) ||
                      (link.alertKey === "projectSetup" &&
                        hasPendingProjectSetup);

                    return (
                      <Link
                        key={`${link.path}-${link.label}`}
                        to={link.path}
                        className={`dash-admin-home__card${
                          isAlert ? " dash-admin-home__card--alert" : ""
                        }`}
                      >
                        <span className="dash-admin-home__card-label">
                          {link.label}
                        </span>
                        {isAlert && (
                          <span
                            className="dash-menu-new-badge"
                            title={
                              link.alertKey === "capability"
                                ? "Pending capability requests"
                                : "Pending project setup requests"
                            }
                          >
                            <span
                              className="dash-menu-new-badge__dot"
                              aria-hidden="true"
                            />
                            New
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
};

export default AdminDashboard;
